import React, { useEffect, useState } from 'react'
import { Button, BUTTON_THEME } from '@st-operations/uikit/src/v2'

import { previewNames } from '_services/api-integrations'

const PAGE = 10

const mappingCell = (row) => {
  if (row.missing) return '—'
  if (row.mapping_id) return row.mapping_id
  return <span className="text-amber-700 font-sans">no ID — skipped</span>
}

const sentCell = (row) => {
  if (row.missing) return ''
  if (row.after) return row.after
  return <span className="text-red-600">nothing left — this audience would be skipped</span>
}

// The audiences this push will send, ten at a time: each one's ID in the
// chosen mapping space, and its name before and after the name changes.
//
// It follows the audiences section - the whole taxonomy, or just the ones
// picked, uploaded or pasted, in the order they came in. A push with no
// taxonomy only ever has a list. Names come from the
// server a page at a time, so they follow the steps as they are edited and the
// transform lives in one place (applyNameChanges) rather than being copied into
// the browser.
const NamePreview = ({ taxonomyId, mappingId, selection, nameChanges }) => {
  const [page, setPage] = useState(0)
  const [rows, setRows] = useState(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState(null)

  const picked = selection?.mode === 'ids'
  const steps = JSON.stringify(nameChanges || [])

  // a different set of audiences starts back on page one
  useEffect(() => setPage(0), [selection, mappingId])

  useEffect(() => {
    if (!mappingId || (!taxonomyId && !picked)) return undefined

    const ids = picked ? selection.audience_ids || [] : null
    const pageIds = picked ? ids.slice(page * PAGE, page * PAGE + PAGE) : null
    if (picked && !pageIds.length) {
      setRows([])
      setTotal(0)
      return undefined
    }

    let cancelled = false
    // debounced, so typing a step does not fire a request per keystroke
    const timer = setTimeout(async () => {
      try {
        const data = await previewNames(
          taxonomyId,
          nameChanges || [],
          picked
            ? { audienceIds: pageIds, mappingId }
            : { skip: page * PAGE, limit: PAGE, mappingId }
        )
        if (cancelled) return
        const items = data?.items || []
        if (picked) {
          const byId = {}
          for (const item of items) byId[item._id] = item
          // one that has left the taxonomy since it was chosen is shown as
          // such, rather than quietly dropped from the page
          setRows(pageIds.map((id) => byId[id] || { _id: id, missing: true }))
          setTotal(ids.length)
        } else {
          setRows(items)
          setTotal(data?.total || 0)
        }
        setError(null)
      } catch (e) {
        if (!cancelled) setError(e?.message || 'could not build the preview')
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [taxonomyId, mappingId, selection, page, steps])

  if (!mappingId) return null

  const start = page * PAGE

  return (
    <div className="mt-6">
      <div className="section-title">Preview</div>
      <div className="section-hint">
        {picked ? 'The audiences you chose' : 'Every audience in the taxonomy'}, as they will be
        sent. Steps run top to bottom, so the order matters.
      </div>

      <If condition={error}>
        <div className="text-red-600 text-sm mb-2">{error}</div>
      </If>

      <Choose>
        <When condition={rows === null}>
          <div className="text-sm text-gray-400">loading…</div>
        </When>
        <When condition={!rows.length}>
          <div className="text-sm text-gray-500">
            {picked ? 'No audiences chosen yet.' : 'This taxonomy has no audiences.'}
          </div>
        </When>
        <Otherwise>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2">Mapping ID</th>
                <th>Name now</th>
                <th>Name sent</th>
              </tr>
            </thead>
            <tbody>
              <For each="row" of={rows}>
                <tr key={row._id} className="border-t border-gray-100">
                  <td className="py-2 font-mono text-xs">{mappingCell(row)}</td>
                  <td className="text-gray-600">
                    {row.missing ? 'no longer in this taxonomy' : row.before}
                  </td>
                  <td>{sentCell(row)}</td>
                </tr>
              </For>
            </tbody>
          </table>

          <If condition={total > PAGE}>
            <div className="flex items-center gap-3 mt-3">
              <Button
                theme={BUTTON_THEME.SECONDARY}
                onClick={() => setPage(page - 1)}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                theme={BUTTON_THEME.SECONDARY}
                onClick={() => setPage(page + 1)}
                disabled={start + PAGE >= total}
              >
                Next
              </Button>
              <span className="text-sm text-gray-500">
                {start + 1}–{Math.min(start + PAGE, total)} of {total}
              </span>
            </div>
          </If>
        </Otherwise>
      </Choose>
    </div>
  )
}

export default NamePreview
